export const EMPTY_CELL = '—';

export type MailRoomFileStatus = 'pend' | 'recv' | 'disp' | 'cust';

/** Null/empty API values display as dash in the table. */
export function displayOrDash(value: string | null | undefined): string {
  const t = typeof value === 'string' ? value.trim() : '';
  return t || EMPTY_CELL;
}

export type CaseSubmissionRequest = {
  /** File reference number (CMS file ref). */
  ref: string;
  fileCategory: string;
  subFileCategory: string;
  agency: string;
  /** Physical file status label from API. */
  statusLabel: string;
  /** CSS pill class for status badge. */
  statusClass: string;
  deliveryMethod: string;
  /** File movement date & time (latest physical file event). */
  movementAt: string;
  custody: string;
  /** Case request id for cover note / PDF API integration. */
  caseRequestId?: string;
  /** Resolved request flow used to choose cover-note endpoints. */
  flow?: import('../utils/wizardCategoryRoute').WizardFlowKind;
  /** @deprecated Use statusLabel — kept for mock rows. */
  fs?: MailRoomFileStatus;
};

export const FS_LABEL: Record<MailRoomFileStatus, string> = {
  pend: 'Pending Receive',
  recv: 'Received',
  disp: 'Dispatched',
  cust: 'In Custody',
};

export const FS_CLASS: Record<MailRoomFileStatus, string> = {
  pend: 'p-pend',
  recv: 'p-recv',
  disp: 'p-disp',
  cust: 'p-cust',
};

function mockRow(
  ref: string,
  fs: MailRoomFileStatus,
  partial: Omit<CaseSubmissionRequest, 'ref' | 'statusLabel' | 'statusClass' | 'fs'>,
): CaseSubmissionRequest {
  return {
    ref,
    fs,
    statusLabel: FS_LABEL[fs],
    statusClass: FS_CLASS[fs],
    ...partial,
  };
}

export const mailRoomRequests: CaseSubmissionRequest[] = [
  mockRow('E/104/2026/AGD', 'pend', {
    fileCategory: 'Civil Division - Advise',
    subFileCategory: 'E - General Legal Advice',
    agency: 'Department of Land Settlement',
    deliveryMethod: 'By Post',
    movementAt: '2026-03-22 10:05',
    custody: EMPTY_CELL,
    caseRequestId: 'mock-e-104-2026',
  }),
  mockRow('WP1/112/2026', 'recv', {
    fileCategory: 'Criminal',
    subFileCategory: 'CH - Child Abuse',
    agency: 'NCPA',
    deliveryMethod: 'Courier',
    movementAt: '2026-03-22 10:05',
    custody: 'Mail Room Officer (Mail Room)',
    caseRequestId: 'mock-wp1-112-2026',
  }),
  mockRow('E/200/2026/AGD', 'disp', {
    fileCategory: 'Civil Appellate Division',
    subFileCategory: 'COA - Court of Appeal',
    agency: 'Sri Lanka Police',
    deliveryMethod: 'By Hand',
    movementAt: '2026-03-22 10:05',
    custody: 'Mr. D. Silva (Processing Unit)',
    caseRequestId: 'mock-e-200-2026',
  }),
  mockRow('PP/05/2026/AGD', 'cust', {
    fileCategory: 'Public Petitions',
    subFileCategory: '—',
    agency: 'Ministry of Health',
    deliveryMethod: 'By Post',
    movementAt: '2026-03-22 10:05',
    custody: 'Mr. D. Silva (Processing Unit)',
    caseRequestId: 'mock-pp-05-2026',
  }),
  mockRow('RTI/12/2026/AGD', 'pend', {
    fileCategory: 'Right to Information',
    subFileCategory: '—',
    agency: 'Ministry of Education',
    deliveryMethod: 'By Hand',
    movementAt: '2026-03-22 10:05',
    custody: '',
    caseRequestId: 'mock-rti-12-2026',
  }),
];

/** Pending arrival — custody must be blank per data dictionary. */
export function isPendingPhysicalFileStatus(status: string): boolean {
  const s = status.toLowerCase();
  return (
    s.includes('pending document') ||
    s.includes('pending receive') ||
    s === 'pending' ||
    s.includes('pending arrival')
  );
}

/** Map API physical file status text to pill CSS class. */
export function physicalFileStatusPillClass(status: string): string {
  const s = status.toLowerCase();
  if (isPendingPhysicalFileStatus(status)) return FS_CLASS.pend;
  if (s.includes('received')) return FS_CLASS.recv;
  if (s.includes('dispatch')) return FS_CLASS.disp;
  if (s.includes('custody')) return FS_CLASS.cust;
  return 'p-pend';
}
